import { useCallback, useEffect, useRef, useState } from 'react';
import { journeyService } from '../services/journeyService';
import type { JourneyChapterTemplateFirestore } from '../services/journeyTypes';
import { useJourneyAccordionParams } from '../hooks/useJourneyAccordionParams';
import { useAuth } from '../../../hooks/useAuth';
import { ChapterSection } from './ChapterSection';
import { JourneyProgressManager } from './JourneyProgressManager';
import { BulkItemRewardsUpdate } from './BulkItemRewardsUpdate';

export const JourneyTemplates = () => {
    const { user } = useAuth();
    const [chapters, setChapters] = useState<JourneyChapterTemplateFirestore[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [progressRefreshTrigger, setProgressRefreshTrigger] = useState(0);
    const hasLoadedOnceRef = useRef(false);

    const {
        openChapterId,
        openSectionIds,
        handleToggleChapter,
        handleToggleSection,
        syncFromLoadedChapters,
        pruneSectionsForChapter,
    } = useJourneyAccordionParams();

    const syncFromLoadedChaptersRef = useRef(syncFromLoadedChapters);
    syncFromLoadedChaptersRef.current = syncFromLoadedChapters;

    const handleJourneyUpdate = () => {
        setProgressRefreshTrigger((prev) => prev + 1);
    };

    const fetchChapters = useCallback(async () => {
        if (hasLoadedOnceRef.current) {
            setIsRefreshing(true);
        } else {
            setIsInitialLoading(true);
        }

        const data = await journeyService.getChapters();
        setChapters(data);
        hasLoadedOnceRef.current = true;
        setIsInitialLoading(false);
        setIsRefreshing(false);

        syncFromLoadedChaptersRef.current(data.map((chapter) => chapter.id!).filter(Boolean));
    }, []);

    useEffect(() => {
        fetchChapters();
    }, [fetchChapters]);

    const pruneSectionsForChapterRef = useRef(pruneSectionsForChapter);
    pruneSectionsForChapterRef.current = pruneSectionsForChapter;

    const handleSectionsLoaded = useCallback((chapterId: string, sectionIds: string[]) => {
        pruneSectionsForChapterRef.current(chapterId, sectionIds);
    }, []);

    if (isInitialLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
                    <p className="mt-4 text-gray-600">Loading journey templates...</p>
                </div>
            </div>
        );
    }

    if (chapters.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-gray-500">No journey templates found. Run the migration to create templates from quest data.</p>
            </div>
        );
    }

    return (
        <div className="relative flex flex-col gap-4">
            {isRefreshing && (
                <div className="absolute inset-0 z-10 flex items-start justify-center bg-gray-50/60 pt-16">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
            )}

            <JourneyProgressManager
                key={progressRefreshTrigger}
                userId={user?.uid ?? ''}
                chapterTemplates={chapters}
                onJourneyUpdate={handleJourneyUpdate}
            />
            <BulkItemRewardsUpdate onUpdated={fetchChapters} />
            <h2 className="text-xl font-semibold text-gray-700">
                {chapters.length} Chapter{chapters.length !== 1 ? 's' : ''}
            </h2>
            {chapters.map((chapter) => (
                <ChapterSection
                    key={chapter.id}
                    chapter={chapter}
                    isOpen={openChapterId === chapter.id}
                    onToggle={() => handleToggleChapter(chapter.id!)}
                    openSectionIds={openSectionIds}
                    onToggleSection={handleToggleSection}
                    onSectionsLoaded={handleSectionsLoaded}
                    onChapterUpdated={fetchChapters}
                />
            ))}
        </div>
    );
};
